import { SiteNav, SiteFooter } from "@/pages/LandingPage";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Linkedin, Instagram } from "lucide-react";

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-us-page">
      <SiteNav />
      <main className="pt-16">
        <section className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ix-red-600 mb-3">About Us</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-ix-black tracking-tight mb-6">Building Infrastructure for Social Impact</h1>
            <p className="text-base text-slate-600 leading-relaxed mb-4">
              ImpactX Bridge is a national infrastructure platform built to transform how CSR teams and grassroots NGOs connect, collaborate, and create real-world impact.
            </p>
            <p className="text-base text-slate-600 leading-relaxed mb-4">
              Today, high-potential social projects struggle to access the right funding. And CSR teams face a maze of unverified proposals, scattered data, and time-consuming coordination. ImpactX Bridge fixes this with verified workflows, intelligent matching, and transparent processes that simplify how funding moves from boardroom to the field.
            </p>
            <p className="text-base text-slate-600 leading-relaxed">
              We're not building a tool. We're building the rails that move trust, capital, and outcomes across the impact economy.
            </p>
          </div>
        </section>

        <Separator />

        <section className="py-20 px-6 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ix-red-600 mb-3">Our Mission</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-ix-black tracking-tight mb-6">From the Founders</h2>
            <blockquote className="border-l-4 border-ix-red-600 pl-6 text-slate-600 italic text-base leading-relaxed mb-12">
              "We've seen the gap from both sides - one of us from inside corporate systems, the other from on-ground NGO experience. We believe technology should simplify trust, not replace it."
            </blockquote>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Asha Joseph */}
              <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="founder-asha">
                <CardContent className="p-0">
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img src="/asha.jpg" alt="Asha Joseph" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-ix-black">Asha Joseph</h3>
                    <p className="text-sm text-ix-red-600 font-medium mb-3">Founder & CEO</p>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">
                      Brings deep CSR regulatory expertise and a vision for digitising India's social funding infrastructure. Passionate about creating transparent systems that connect capital with impact at the grassroots level.
                    </p>
                    <div className="flex items-center gap-3">
                      <a href="https://www.linkedin.com/in/ashajosephvadakkan" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-700 transition-colors" data-testid="asha-linkedin">
                        <Linkedin className="w-4 h-4" />
                      </a>
                      <a href="https://www.instagram.com/a_s_h_a_vadakkan" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-pink-50 hover:text-pink-600 transition-colors" data-testid="asha-instagram">
                        <Instagram className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Aswin Joseph */}
              <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="founder-aswin">
                <CardContent className="p-0">
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img src="/aswin.jpg" alt="Aswin Joseph" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-ix-black">Aswin Joseph</h3>
                    <p className="text-sm text-ix-red-600 font-medium mb-3">Co-founder & COO</p>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">
                      Drives product innovation and research, bringing lean design thinking and systems-level insight to build scalable infrastructure for the social impact sector in India.
                    </p>
                    <div className="flex items-center gap-3">
                      <a href="https://www.linkedin.com/in/vadakkanaswin" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-700 transition-colors" data-testid="aswin-linkedin">
                        <Linkedin className="w-4 h-4" />
                      </a>
                      <a href="https://www.instagram.com/aswin.vadakkan" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-pink-50 hover:text-pink-600 transition-colors" data-testid="aswin-instagram">
                        <Instagram className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
